import { SocialAuthService } from '@abacritt/angularx-social-login';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  PLATFORM_ID,
  AfterViewInit,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { authLogin, authSocialLogin } from 'libs/shared/data/stores/auth/auth.actions';
import { selectAuthErrors, getAuthUser } from 'libs/shared/data/stores/auth/auth.selectors';
import { variables } from 'libs/shared/utils/variables/variables';
import { RecaptchaModule } from 'ng-recaptcha';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { TooltipModule } from 'primeng/tooltip';

import { UserService } from '@offconon/core-api';
import { FormErrorsComponent } from '@offconon/shared/ui/form-elements/form-errors';

import { SocialLoginComponent } from '../../components/social-login/social-login.component';
import { AuthService } from '../../services/auth.service';
import { RecaptchaService } from '../../services/recaptcha.service';

@Component({
  selector: 'offconon-login-form',
  templateUrl: './login-form.component.html',
  imports: [
    CardModule,
    FormsModule,
    ReactiveFormsModule,
    TooltipModule,
    InputTextModule,
    FormErrorsComponent,
    RouterLink,
    PasswordModule,
    ButtonModule,
    SocialLoginComponent,
    TranslateModule,
    RecaptchaModule,
    CommonModule,
  ],
})
export class LoginFormComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthService);
  private readonly ngZone = inject(NgZone);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  readonly recaptchaService = inject(RecaptchaService);

  loading = false;
  returnUrl: string;

  form = inject(FormBuilder).group({
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    password: new FormControl<string>('', [Validators.required]),
  });

  socialLogin(user: any) {
    this.loading = true;
    this.store.dispatch(
      authSocialLogin({
        auth: {
          social_id: user.id,
          social_type: user.provider.toLowerCase() as any,
          token: user.idToken,
        },
        returnUrl: this.returnUrl,
      }),
    );
    // Reset reCAPTCHA state after social login
    this.recaptchaService.resetCaptchaState();
  }

  constructor() {
    this.socialAuthService.authState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      this.socialLogin(user);
    });

    this.store
      .select(selectAuthErrors)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((err) => {
        if (err?.error) {
          if (err.headers.normalizedNames.size > 0) {
            if (
              err.error.errors &&
              Array.isArray(err.error.errors) &&
              err.error.errors.length > 0
            ) {
              if (err.error.errors[0].error === 'NotFoundUserNameOrPassword') {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('Error'),
                  detail: this.translate.instant(
                    'We could not find this email address and password combination.',
                  ),
                });
              } else if (err.error && err.error.errors[0].error !== 'NotFoundUserNameOrPassword') {
                for (const key in err.error.errors) {
                  this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('Something went wrong. Please try again.'),
                    detail: this.translate.instant('' + err.error.errors[key].error),
                  });
                }
              } else {
              }
            }
          }
        }
      });
  }

  ngOnInit(): void {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || variables.defaultRedirectPage;

    // Check if user is already logged in and redirect if needed
    this.store
      .select(getAuthUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (user) {
          // User is already logged in, redirect to return URL or default page
          this.router.navigateByUrl(this.returnUrl);
        }
      });

    // Listen for auth errors
    this.store
      .select(selectAuthErrors)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((errors: any[]) => {
        if (errors && errors.length > 0) {
          this.loading = false;
          errors.forEach((error: string) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('Error'),
              detail: error,
            });
          });
        } else {
          // If no errors and we were loading, it means login was successful
          // Reset reCAPTCHA state
          if (this.loading) {
            this.recaptchaService.resetCaptchaState();
          }
        }
      });

    // Check for social auth
    this.socialAuthService.authState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user) {
        this.socialLogin(user);
      }
    });

    if (this.route.snapshot.queryParamMap.get('verified')) {
      if (this.route.snapshot.queryParamMap.get('verified') === 'true') {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('Successful'),
          detail: this.translate.instant('Your account has been verified. You can now login.'),
        });
        this.returnUrl = '/me';
      } else {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Error'),
          detail: this.translate.instant(
            'Something went wrong.Your account has not been verified. Please try again.',
          ),
        });
      }
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.recaptchaService.loadRecaptchaScript();
    }

    this.store
      .select(getAuthUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user) => {
        if (user) {
          // If user is authenticated, redirect to the return URL
          this.ngZone.run(() => {
            this.router.navigateByUrl(this.returnUrl);
          });
        }
      });
  }

  ngOnDestroy(): void {
    this.recaptchaService.hideRecaptchaWidget();
  }

  login(): void {
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    if (isPlatformBrowser(this.platformId)) {
      this.executeRecaptcha();
    } else {
      this.submitLogin();
    }
  }

  executeRecaptcha(): void {
    this.recaptchaService
      .executeRecaptcha('login')
      .then((token) => {
        if (token) {
          this.resolved(token);
        } else {
          this.submitLogin();
        }
      })
      .catch(() => {
        this.loading = false;
      });
  }

  resolved(token: string) {
    this.recaptchaService.recaptchaToken.set(token);
    this.recaptchaService.captchaVerified.set(true);
    this.recaptchaService.hideRecaptchaWidget();
    this.submitLogin();
  }

  submitLogin(): void {
    this.store.dispatch(
      authLogin({
        auth: {
          email: this.form.value.email as string,
          password: this.form.value.password as string,
        },
        returnUrl: this.returnUrl,
      }),
    );
    this.loading = false;
  }

  openAcademy(): void {
    window.open('/academy/offconon-passport/1/signing-in-to-your-account', '_blank');
  }

  buttonClicked(link: any): void {
    this.router.navigate([link]);
  }
}
